// uses ./appStatusForm.less
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import STATUS from '@integrations/pint-appstore-commons/src/enums/APP_STATUS.json';
import TRANSITION from '@integrations/pint-appstore-commons/src/enums/APP_STATUS_TRANSITION.json';
import APP_TYPE from '@integrations/pint-appstore-commons/src/enums/APP_TYPE.json';
import EGNYTE_PLATFORM from '@integrations/pint-appstore-commons/src/enums/EGNYTE_PLATFORM.json';
import { getSettings } from '../../utils/utils';
import {
    closeStatusForm,
    changeAppSettings,
    getAddedGroups,
    fetchDomainSettingsToken,
    fetchStatusFrom,
    updateAppTransition,
    updateSelectedGroups,
    updateCollaborationAppsPriorityList,
    updateCoeditPolicy,
    updateCustomData,
} from '../common/appStatusActionCreators';
import { clickLink } from '../tracking/trackingActionCreators';
import ConfirmationModal from '../common/ConfirmationModal';

import DefaultEditorChooser from './defaultEditorChooser/DefaultEditorChooser';
import AppStatusChooser from './appStatusChooser/AppStatusChooser';
import GroupsChooser from './groupsChooser/GroupsChooser';
import AppDomainSettings from './appDomainSettings/AppDomainSettings';
import Loading from '../common/Loading';
import settingsPopupObserver from '../../components/settingsPopupObserver';
import { checkFormHasChanges, getAppSettingsQuery } from './appStatusFormUtils';
import { MS_OFFICE_DESKTOP } from './MSOfficeDesktopAdminCheckboxes/mSOfficeDesktopCheckboxFields.enums';
import { Editor, SelectedGroup, ExtendedRootState, AppId } from '../common/interfaces';
import { CoeditPolicies, CoeditPolicy } from '../editors/editorsReducer';
import { MSOfficeDesktopModal } from './MSOfficeDesktopModal/MSOfficeDesktopModal';
import { MSOfficeDesktopEditModal } from './MSOfficeDesktopModal/MSOfficeDesktopEditModal';
import ServiceNowCustomForm from './appCustomForm/ServiceNowCustomForm';

let searchBarCache = {};

const settings = getSettings();

type CustomData = {
    [key: string]: string;
};

type AppSettings = {
    appId: string;
    editors?: {
        collaborationAppsHierarchy: string[];
    };
    customData?: CustomData;
};

// TODO rename it to AdminSettingsModal
/* eslint-disable-next-line complexity */
function AppStatusForm(props: {
    // TODO most of typings should be derived automatically straight from Redux store
    appId?: string;
    editorsApps: Editor[];
    collaborationAppsHierarchy: AppId[];
    collaborationAppsHierarchyAfterUpdate: AppId[];
    coeditPolicy: CoeditPolicy;
    [key: string]: any;
}) {
    const [hasClientIdChanged, setHasClientIdChanged] = useState(false);
    const [hasSelectedGroupsChanged, setHasSelectedGroupsChanged] = useState(false);

    const [shouldBeAppStatusChooserDisabled, setShouldBeAppStatusChooserDisabled] = useState(false);

    const activeMSOfficeDesktop = useMemo(() => {
        const mSOfficeDesktopApp = props.editorsApps.find((el) => el.appId === MS_OFFICE_DESKTOP);

        return mSOfficeDesktopApp ? mSOfficeDesktopApp.status !== STATUS.DISABLED : false;
    }, [props.editorsApps]);

    const updateTransition = (transition: string) => {
        props.dispatchUpdateAppTransition(transition);
        setHasSelectedGroupsChanged(props.status !== STATUS.ADDED_FOR_DOMAIN_GROUPS);
        if (transition === TRANSITION.DISABLE) {
            props.dispatchUpdateCollaborationAppsPriorityList(
                props.collaborationAppsHierarchyAfterUpdate.filter((appId) => appId !== props.appId)
            );
        } else if (
            isEditorApp &&
            props.collaborationAppsHierarchy.length > 0 &&
            !props.collaborationAppsHierarchyAfterUpdate.find((appId) => appId === props.appId)
        ) {
            props.dispatchUpdateCollaborationAppsPriorityList([
                ...props.collaborationAppsHierarchyAfterUpdate,
                editorDetails.appId,
            ]);
        }
    };
    const updateSelectedGroups = (groups: SelectedGroup[]) => {
        props.dispatchUpdateSelectedGroups(groups);
        setHasSelectedGroupsChanged(true);
    };

    const updateCollaborationAppsPriorityList = (editor: AppId | null) => {
        if (editor === null) {
            props.dispatchUpdateCollaborationAppsPriorityList([
                ...props.collaborationAppsHierarchyAfterUpdate.filter((appId) => appId !== props.appId),
            ]);
        } else {
            let newHierarchy = props.collaborationAppsHierarchyAfterUpdate.filter((appId) => appId !== props.appId);
            newHierarchy = [editor, ...newHierarchy];
            props.dispatchUpdateCollaborationAppsPriorityList(newHierarchy);
        }
    };

    useEffect(() => {
        if (props.appId !== MS_OFFICE_DESKTOP) {
            return setShouldBeAppStatusChooserDisabled(false);
        }
        if (activeMSOfficeDesktop) {
            return setShouldBeAppStatusChooserDisabled(false);
        }
    }, [props.appId, activeMSOfficeDesktop]);

    useEffect(() => {
        if (shouldBeAppStatusChooserDisabled) updateTransition(TRANSITION.DISABLE);
    }, [shouldBeAppStatusChooserDisabled]);

    useEffect(() => {
        if (props.appId) {
            searchBarCache = {};
            if (props.groups?.length > 0) {
                props.getAddedGroups(props.appId, props.groups);
            }
            if (props.domainSettings) {
                props.dispatchFetchDomainSettingsToken(props.appId);
            }
            props.dispatchUpdateCollaborationAppsPriorityList(props.collaborationAppsHierarchy);
            settingsPopupObserver.onAppSettingsOpen(props.appId);
        }
    }, [props.appId]);

    const appSettingsQuery: AppSettings = useMemo(
        () =>
            getAppSettingsQuery({
                chosenTransition: props.transition,
                selectedGroups: props.selectedGroups,
                status: props.status,
                collaborationAppsHierarchy: props.collaborationAppsHierarchy,
                collaborationAppsHierarchyAfterUpdate: props.collaborationAppsHierarchyAfterUpdate,
                appId: props.appId,
            }),
        [
            props.transition,
            props.selectedGroups,
            props.status,
            props.collaborationAppsHierarchy,
            props.collaborationAppsHierarchyAfterUpdate,
            props.appId,
        ]
    );

    const handleConfirm = () => {
        const queryHasChanges = checkFormHasChanges(appSettingsQuery, hasSelectedGroupsChanged);
        if (queryHasChanges) {
            const hasDefaultAppChanged = !!appSettingsQuery.editors;
            const wasTheAppSetAsDefault = hasDefaultAppChanged
                ? appSettingsQuery.editors?.collaborationAppsHierarchy[0] === appSettingsQuery.appId
                : null;
            props.dispatchChangeAppSettings(appSettingsQuery, wasTheAppSetAsDefault, props.isProtectConfigurable);
        }
        if (hasClientIdChanged && clientId) {
            props.dispatchUpdateCustomData(props.appId, { clientId });
        }
        props.dispatchCloseStatusForm();
        settingsPopupObserver.onAppSettingsClose();
    };

    const isUIIntegration = props.type === APP_TYPE.EGNYTE_UI_INTEGRATION;
    const areGroupsHidden =
        Boolean(settings && settings.toggles && settings.toggles.hideGroups) || props.isProtectConfigurable;
    const hasFormChanges = checkFormHasChanges(appSettingsQuery, hasSelectedGroupsChanged);
    const title = <div className="sg-modal-title">{props.name}</div>;
    const shouldDisplayGroupsChooser = props.transition === TRANSITION.GROUP;

    // editors a.k.a collaboration apps section
    const editorDetails = props.editorsApps.find((editor) => editor.appId === props.appId);
    const isEditorApp = !!editorDetails;
    const shouldDisplayDefaultEditorChooser = isEditorApp && props.transition !== TRANSITION.DISABLE;
    const isTopPriorityEditor = props.collaborationAppsHierarchyAfterUpdate[0] === props.appId;
    const topPriorityEditor =
        props.collaborationAppsHierarchyAfterUpdate.length === 0
            ? null
            : (props.editorsApps.find(
                  (editor) => editor.appId === props.collaborationAppsHierarchyAfterUpdate[0]
              ) as Editor);

    const GDRIVE_APP_ID = 'gdocs';
    const isGoogleDocs = props.appId === GDRIVE_APP_ID;

    // This should be displayed when domain settings are not required or missing.
    // When domain settings are marked as required and present for given app we display component based on confirmation
    const domainConfigurationNotRequiredOrAlreadyConfigured =
        !props.domainSettingsRequired ||
        (props.domainSettingsRequired && isGoogleDocs) ||
        !props.domainSettings ||
        props.configurationStatus?.[EGNYTE_PLATFORM.COLLABORATE]?.isConfigured;
    const shouldDisplayAppStatusDropdown = isUIIntegration && domainConfigurationNotRequiredOrAlreadyConfigured;

    const clickLink = (e: any) => {
        props.dispatchClickLink(props.appId, e.target.getAttribute('href'));
    };

    const [clientId, setClientId] = useState(props.customData?.clientId || '');

    if (!props.appId || !props.areEditorsFetched) {
        return null;
    }

    const handleMSOfficeConfirm = () => {
        props.dispatchCloseStatusForm();
        settingsPopupObserver.onAppSettingsClose();
    };

    if (props.appId === MS_OFFICE_DESKTOP && !activeMSOfficeDesktop) {
        return (
            <MSOfficeDesktopModal
                appId={props.appId}
                name={props.name}
                onClose={() => {
                    props.dispatchCloseStatusForm();
                    settingsPopupObserver.onAppSettingsClose();
                }}
                onConfirm={handleMSOfficeConfirm}
                dispatchChangeAppSettings={props.dispatchChangeAppSettings}
                dispatchUpdateCoeditPolicy={props.dispatchUpdateCoeditPolicy}
                collaborationAppsHierarchy={props.collaborationAppsHierarchy}
            />
        );
    }
    if (props.appId === MS_OFFICE_DESKTOP && activeMSOfficeDesktop) {
        return (
            <MSOfficeDesktopEditModal
                name={props.name}
                transition={props.transition}
                areGroupsHidden={areGroupsHidden}
                shouldBeAppStatusChooserDisabled={shouldBeAppStatusChooserDisabled}
                coeditPolicy={props.coeditPolicy}
                collaborationAppsHierarchy={props.collaborationAppsHierarchy}
                dispatchChangeAppSettings={props.dispatchChangeAppSettings}
                dispatchUpdateCoeditPolicy={props.dispatchUpdateCoeditPolicy}
                appId={props.appId}
                onClose={handleMSOfficeConfirm}
                title={title}
                areGroupsFetching={props.areGroupsFetching}
                selectedGroups={props.selectedGroups}
                searchBarCache={props.searchBarCache}
            />
        );
    }

    return (
        <ConfirmationModal
            className="ast-app-status-form"
            title={title}
            confirmLabel="Save"
            cancelLabel="Cancel"
            confirmHandler={handleConfirm}
            closeHandler={() => {
                props.dispatchCloseStatusForm();
                settingsPopupObserver.onAppSettingsClose();
                setHasClientIdChanged(false);
            }}
            childrenWrapperClassName="ast-app-status-form__content"
            buttonWrapperClassName={shouldDisplayGroupsChooser ? 'sg-modal-buttons__switchPlacing' : ''}
            isDisabled={!hasFormChanges && !hasClientIdChanged}
            hideCloseButton
        >
            {props.domainSettings && (
                <Loading isLoaded={!props.isDomainSettingsTokenFetching}>
                    <AppDomainSettings
                        appId={props.appId}
                        userManual={props.userManual}
                        settingsURL={props.domainSettings}
                        domainSettingsEnabledFromFeatureFlag={props.domainSettingsEnabledFromFeatureFlag}
                        onClickLink={clickLink}
                        isConfigured={{
                            collaborate:
                                props.configurationStatus?.[EGNYTE_PLATFORM.COLLABORATE]?.isConfigured || false,
                            secureAndGovern:
                                props.configurationStatus?.[EGNYTE_PLATFORM.SECURE_AND_GOVERN]?.isConfigured || false,
                        }}
                        dispatchFetchStatusForm={props.dispatchFetchStatusForm}
                        domainSettingsRequired={props.domainSettingsRequired}
                        {...props.domainSettingsToken}
                    />
                </Loading>
            )}

            {shouldDisplayAppStatusDropdown && (
                <AppStatusChooser
                    name={props.name}
                    transition={props.transition}
                    handleChange={updateTransition}
                    hideGroups={areGroupsHidden}
                    disabled={shouldBeAppStatusChooserDisabled}
                    hideAllowedUsers={props.isProtectConfigurable}
                />
            )}
            {props.appId === 'externalticketdetails' && props.transition !== TRANSITION.DISABLE && (
                <ServiceNowCustomForm
                    appId={props.appId}
                    clientId={props.customData?.clientId}
                    isLoading={false}
                    handleChange={(value) => {
                        setClientId(value);
                        if (!hasClientIdChanged) {
                            setHasClientIdChanged(true);
                        }
                    }}
                />
            )}
            {shouldDisplayGroupsChooser && (
                <GroupsChooser
                    isLoading={props.areGroupsFetching}
                    isHidden={!shouldDisplayGroupsChooser}
                    selectedGroups={props.selectedGroups}
                    handleChange={updateSelectedGroups}
                    searchBarCache={searchBarCache}
                />
            )}
            {shouldDisplayDefaultEditorChooser && (
                <DefaultEditorChooser
                    appId={props.appId}
                    isLoading={props.areEditorsFetching}
                    handleChange={updateCollaborationAppsPriorityList}
                    topPriorityEditor={topPriorityEditor}
                    editorDetails={editorDetails}
                    isTopPriorityEditor={isTopPriorityEditor}
                />
            )}
        </ConfirmationModal>
    );
}

const mapDispatchToProps = {
    dispatchCloseStatusForm: closeStatusForm,
    dispatchChangeAppSettings: changeAppSettings,
    getAddedGroups,
    dispatchFetchDomainSettingsToken: fetchDomainSettingsToken,
    dispatchClickLink: clickLink,
    dispatchFetchStatusForm: fetchStatusFrom,
    dispatchUpdateAppTransition: updateAppTransition,
    dispatchUpdateSelectedGroups: updateSelectedGroups,
    dispatchUpdateCollaborationAppsPriorityList: updateCollaborationAppsPriorityList,
    dispatchUpdateCoeditPolicy: updateCoeditPolicy,
    dispatchUpdateCustomData: updateCustomData,
};
const AppStatusFormWrapper = connect((state) => {
    const { appStatusForm = {}, editors = {} } = state as ExtendedRootState;
    const {
        name,
        status,
        groups,
        domainSettings,
        userManual,
        appId,
        configurationStatus,
        type,
        isAvailable,
        domainSettingsRequired,
        isDomainSettingsTokenFetching,
        domainSettingsToken,
        areGroupsFetching,
        stateUpdate,
        domainSettingsEnabledFromFeatureFlag,
        isProtectConfigurable,
        customData,
    } = appStatusForm;
    const {
        editorsApps = [],
        collaborationAppsHierarchy,
        isFetching: areEditorsFetching,
        isFetched: areEditorsFetched,
        coeditPolicy = {
            word: CoeditPolicies.Ask, // default CoeditPolicies
            excel: CoeditPolicies.Ask,
            powerpoint: CoeditPolicies.Ask,
            userCanOverwrite: false,
            arePoliciesEnabled: false,
        },
    }: {
        editorsApps: Editor[];
        collaborationAppsHierarchy: AppId[];
        isFetching: boolean;
        isFetched: boolean;
        coeditPolicy: CoeditPolicy;
    } = editors;

    return {
        // appStatusForm
        name,
        status,
        groups,
        domainSettings,
        userManual,
        appId,
        configurationStatus,
        type,
        isAvailable,
        domainSettingsRequired,
        isDomainSettingsTokenFetching,
        domainSettingsToken,
        areGroupsFetching,
        domainSettingsEnabledFromFeatureFlag,
        isProtectConfigurable,
        customData,

        // editors
        areEditorsFetching,
        areEditorsFetched,
        editorsApps,
        collaborationAppsHierarchy,
        transition: stateUpdate.transition,
        selectedGroups: stateUpdate.selectedGroups,
        collaborationAppsHierarchyAfterUpdate: stateUpdate.collaborationAppsHierarchyAfterUpdate,
        coeditPolicy,
    };
}, mapDispatchToProps)(AppStatusForm);

export default AppStatusFormWrapper;
